import React, { createContext, useContext, useEffect, useRef, useState } from "react";

// ============================================================================
// Adaptive Environment Engine
// Reads visitor behaviour (scroll velocity, dwell, return visits) and exposes
// reactive state every component can subscribe to. Powers the "the system
// understands me" feeling.
// ============================================================================

const AdaptiveCtx = createContext(null);

const STORAGE = {
    visits: "apib_visit_count",
    lastVisit: "apib_last_visit",
    visitedSections: "apib_visited_sections",
    sectionDwell: "apib_section_dwell",
};

export function AdaptiveProvider({ children }) {
    const [scrollVelocity, setScrollVelocity] = useState(0); // px / 16ms, smoothed
    const [activeSection, setActiveSection] = useState(null);
    const [visitedSections, setVisitedSections] = useState(() => {
        try { return new Set(JSON.parse(localStorage.getItem(STORAGE.visitedSections) || "[]")); }
        catch { return new Set(); }
    });
    const [sectionDwell, setSectionDwell] = useState(() => {
        try { return JSON.parse(localStorage.getItem(STORAGE.sectionDwell) || "{}"); }
        catch { return {}; }
    });
    const [visitCount, setVisitCount] = useState(() => {
        const n = parseInt(localStorage.getItem(STORAGE.visits) || "0", 10);
        return Number.isNaN(n) ? 0 : n;
    });
    const [returningVisitor, setReturningVisitor] = useState(false);

    const lastScrollY = useRef(0);
    const lastTs = useRef(0);
    const velRef = useRef(0);
    const sectionEnterTs = useRef(null);
    const activeRef = useRef(null);

    // Initial visit accounting
    useEffect(() => {
        const last = parseInt(localStorage.getItem(STORAGE.lastVisit) || "0", 10);
        const isReturn = !!last && Date.now() - last < 1000 * 60 * 60 * 24 * 30;
        setReturningVisitor(isReturn);
        const n = visitCount + 1;
        setVisitCount(n);
        localStorage.setItem(STORAGE.visits, String(n));
        localStorage.setItem(STORAGE.lastVisit, String(Date.now()));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Smoothed scroll velocity
    useEffect(() => {
        let raf = 0;
        const tick = (ts) => {
            const y = window.scrollY;
            const dt = lastTs.current ? Math.max(1, ts - lastTs.current) : 16;
            const inst = (y - lastScrollY.current) / dt; // px/ms
            velRef.current = velRef.current * 0.82 + inst * 0.18;
            lastScrollY.current = y;
            lastTs.current = ts;
            setScrollVelocity(Math.abs(velRef.current));
            raf = requestAnimationFrame(tick);
        };
        raf = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(raf);
    }, []);

    // Section visibility tracker → fires `markVisited` after 1.5s of dwell
    useEffect(() => {
        const sections = ["hero", "evolution", "reveal", "backbone", "fabric", "simulator",
                          "products", "developers", "sandbox", "command", "singularity", "connect"];
        const els = sections.map(id => document.getElementById(id)).filter(Boolean);
        if (els.length === 0) return;

        const io = new IntersectionObserver(
            (entries) => {
                // pick the most visible one
                let best = null, bestRatio = 0;
                entries.forEach(e => {
                    if (e.intersectionRatio > bestRatio) {
                        bestRatio = e.intersectionRatio;
                        best = e.target.id;
                    }
                });
                if (best && best !== activeRef.current) {
                    // flush previous dwell
                    if (activeRef.current && sectionEnterTs.current) {
                        const ms = Date.now() - sectionEnterTs.current;
                        setSectionDwell(prev => {
                            const next = { ...prev, [activeRef.current]: (prev[activeRef.current] || 0) + ms };
                            try { localStorage.setItem(STORAGE.sectionDwell, JSON.stringify(next)); } catch {}
                            return next;
                        });
                    }
                    activeRef.current = best;
                    sectionEnterTs.current = Date.now();
                    setActiveSection(best);

                    // Mark visited after 1.5s
                    setTimeout(() => {
                        if (activeRef.current === best) {
                            setVisitedSections(prev => {
                                if (prev.has(best)) return prev;
                                const next = new Set(prev); next.add(best);
                                try { localStorage.setItem(STORAGE.visitedSections, JSON.stringify([...next])); } catch {}
                                return next;
                            });
                        }
                    }, 1500);
                }
            },
            { threshold: [0, 0.25, 0.5, 0.75, 1] }
        );
        els.forEach(e => io.observe(e));
        return () => io.disconnect();
    });

    return (
        <AdaptiveCtx.Provider value={{
            scrollVelocity, activeSection,
            visitedSections, sectionDwell,
            visitCount, returningVisitor,
        }}>
            {children}
        </AdaptiveCtx.Provider>
    );
}

export function useAdaptive() {
    return useContext(AdaptiveCtx) || {
        scrollVelocity: 0, activeSection: null,
        visitedSections: new Set(), sectionDwell: {},
        visitCount: 1, returningVisitor: false,
    };
}
